import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Input, Select, InputNumber, Space, Radio, Col, Row, Divider, Tooltip } from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import { useAsyncAxios, utilAxiosWithAppToken } from "../utils/customAxios";

import * as Common from "../commons/common";
import * as Types from "../commons/typeDTO";
import * as Request from "../commons/request";
import * as Utils from "../utils/utils";

import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import CustomDatePicker from "./CustomDatePicker";
import styles from "./ModalCoupon.module.css";

const numberFormatter = (value: string | undefined) => {
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const onScroll = (e: any) => {
    if (e.target?.classList.contains("scrolling") === false) {
        e.target?.classList.add("scrolling");
    }
    onScrollStop(e);
};
let scrollStopTimer: any = null;
const onScrollStop = (e: any) => {
    if (scrollStopTimer) {
        clearTimeout(scrollStopTimer);
        scrollStopTimer = null;
    }
    scrollStopTimer = setTimeout(() => {
        if (e.target?.classList.contains("scrolling")) {
            e.target?.classList.remove("scrolling");
        }
    }, 500);
};

function ModalCoupon({
    open,
    coupon,
    onChangedOpen,
    onChangeCouponInfo,
}: {
    open: boolean;
    coupon?: Types.CouponDto;
    onChangedOpen: (open: boolean) => void;
    onChangeCouponInfo?: (type: "register" | "update", value: any) => void;
}) {
    const [form] = Form.useForm();
    const { Option } = Select;

    const [currentCoupon, setCurrentCoupon] = useState<Types.CouponDto>();

    const [registerExpiryDate, setRegisterExpiryDate] = useState<string | undefined>();
    const [effectiveDate, setEffectiveDate] = useState<string | undefined>();
    const [expiredDate, setExpiredDate] = useState<string | undefined>();
    const [rentPaidItems, setRentPaidItems] = useState<Array<Types.RentPaidItemDto>>();

    useEffect(() => {
        requestGetRentPaidItems();

        document.querySelectorAll(".ant-modal-body").forEach((modalBody) => {
            modalBody.addEventListener("scroll", onScroll);
        });
        return () => {
            document.querySelectorAll(".ant-modal-body").forEach((modalBody) => {
                modalBody.removeEventListener("scroll", onScroll);
            });
        };
    }, []);

    useEffect(() => {
        form.resetFields();
    }, [open]);

    useEffect(() => {
        if (!coupon) return;

        setCurrentCoupon(coupon);

        form.setFieldsValue({
            ...coupon,
            registerExpiryDate: coupon.registerExpiryDate && dayjs(coupon.registerExpiryDate),
            effectiveDate: coupon.effectiveDate && dayjs(coupon.effectiveDate),
            expiredDate: coupon.expiredDate && dayjs(coupon.expiredDate),
            discountUnit: coupon.discountUnit ?? "PERCENT",
            ...(coupon.couponType === "DAYS" && {
                couponTypeDays: "DAYS",
                couponTypeTotal: "",
            }),
            ...(coupon.couponType === "TOTAL" && {
                couponTypeTotal: "TOTAL",
                weekdayDiscountAmount: coupon.weekdayDiscountedPrice,
                weekendDiscountAmount: coupon.weekendDiscountedPrice,
                couponTypeDays: "",
                weekdayDiscountedPrice: undefined,
                weekendDiscountedPrice: undefined,
            }),
        });

        setRegisterExpiryDate(coupon?.registerExpiryDate);
        setEffectiveDate(coupon?.effectiveDate);
        setExpiredDate(coupon?.expiredDate);
    }, [coupon]);

    //rentPaidItems
    const requestAxiosGetRentPaidItems = async () => {
        const response = await utilAxiosWithAppToken().get(Request.RENT_PAID_ITEMS_URL);
        return response.data;
    };

    const {
        loading: loadingGetRentPaidItems,
        error: errorGetRentPaidItems,
        data: resultGetRentPaidItems,
        execute: requestGetRentPaidItems,
    } = useAsyncAxios(requestAxiosGetRentPaidItems);

    useEffect(() => {
        if (!resultGetRentPaidItems) return;
        console.log("resultGetRentPaidItems", resultGetRentPaidItems);

        setRentPaidItems(resultGetRentPaidItems.rentPaidItems);
    }, [resultGetRentPaidItems]);

    useEffect(() => {
        if (!errorGetRentPaidItems) return;
        console.log("errorGetRentPaidItems", errorGetRentPaidItems);
    }, [errorGetRentPaidItems]);

    const handleClose = () => {
        onChangedOpen(false);
    };

    const onFinish = (values: any) => {
        if (values.effectiveDate > values.expiredDate || (values.registerExpiryDate && values.effectiveDate > values.registerExpiryDate)) {
            Modal.error({
                title: "쿠폰 사용일 오류",
                content: (
                    <>
                        쿠폰 사용 만료일과 쿠폰코드 등록 마감일은
                        <br />
                        쿠폰 사용 시작일 전으로 설정할 수 없습니다.
                    </>
                ),
                okText: "확인",
                onOk() {},
                centered: true,
            });
            return;
        }
        if (!values.couponTypeDays && !values.couponTypeTotal && !values.rentPaidItemId) {
            Modal.error({
                title: "입력된 데이터 없음",
                content: "설정할 쿠폰 값을 입력해 주세요.",
                okText: "확인",
                onOk() {},
                centered: true,
            });
            return;
        }
        if (
            (values.couponTypeDays && values.couponTypeTotal) ||
            (values.couponTypeDays && values.rentPaidItemId) ||
            (values.couponTypeTotal && values.rentPaidItemId)
        ) {
            Modal.error({
                title: "입력 데이터 오류",
                content: "금액고정/금액할인/유료대여품목 중 하나만 등록하세요.",
                okText: "확인",
                onOk() {},
                centered: true,
            });
            return;
        }

        onChangedOpen(false);

        values.registerExpiryDate = registerExpiryDate ?? expiredDate;
        values.effectiveDate = effectiveDate;
        values.expiredDate = expiredDate;

        values.couponType = values.couponTypeDays || values.couponTypeTotal || null;
        if (values.couponTypeDays) {
            values.discountPrice = null;
            values.discountUnit = null;
        } else if (values.couponTypeTotal) {
            values.weekdayDiscountedPrice = values.weekdayDiscountAmount;
            values.weekendDiscountedPrice = values.weekendDiscountAmount;
            values.effectiveDays = null;
        } else {
            values.weekdayDiscountedPrice = null;
            values.weekendDiscountedPrice = null;
            values.effectiveDays = null;
            values.discountPrice = null;
            values.discountUnit = null;
        }

        values.weekdayAvailable =
            (values.weekdayDiscountedPrice !== null && values.weekdayDiscountedPrice >= 0) ||
            (values.discountPrice !== null && values.discountPrice >= 0) ||
            values.rentPaidItemId > 0;
        values.weekendAvailable =
            (values.weekendDiscountedPrice !== null && values.weekendDiscountedPrice >= 0) ||
            (values.discountPrice !== null && values.discountPrice >= 0) ||
            values.rentPaidItemId > 0;

        const tempData = { ...currentCoupon, ...values };
        console.log("tempData", tempData);

        onChangeCouponInfo && onChangeCouponInfo(currentCoupon ? "update" : "register", tempData);
    };

    return (
        <Modal
            centered
            forceRender
            closable={false}
            className="login hide-scrollbar"
            open={open}
            title={currentCoupon ? "쿠폰 수정" : "쿠폰 추가"}
            onOk={handleClose}
            onCancel={handleClose}
            footer={[
                <Button key="back" onClick={handleClose}>
                    취소
                </Button>,
                <Button key="setPassword" type="primary" onClick={() => form.submit()}>
                    {currentCoupon ? "수정" : "추가"}
                </Button>,
            ]}
        >
            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                className="modalCoupon"
                initialValues={{
                    couponCoverage: "RENT",
                    multiCoupon: false,
                    showSerialNumber: false,
                    couponTypeDays: "",
                    couponTypeTotal: "",
                    discountUnit: "PERCENT",
                }}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item name="couponCoverage" label="카테고리" rules={[{ required: true, message: "카테고리를 선택하세요." }]}>
                            <Select
                                options={[
                                    { value: "RENT", label: "렌트 쿠폰" },
                                    { value: "ORDER", label: "판매 쿠폰" },
                                    { value: "B2B", label: "B2B 쿠폰" },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="couponOrder" label="쿠폰 정렬 순번">
                            <Input placeholder="쿠폰 목록에 표시되는 정렬 순번" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Form.Item name="multiCoupon" label="사용 분류" rules={[{ required: true, message: "사용 분류를 선택하세요." }]}>
                            <Radio.Group>
                                <Radio value={false}>단독 쿠폰</Radio>
                                <Radio value={true}>중복 쿠폰</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="showSerialNumber" label="쿠폰코드 표시" rules={[{ required: true, message: "쿠폰코드 표시 여부를 선택하세요." }]}>
                            <Radio.Group>
                                <Radio value={true}>○</Radio>
                                <Radio value={false}>✕</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="couponName" label="쿠폰제목" rules={[{ required: true, message: "쿠폰제목을 입력하세요." }]}>
                    <Input placeholder="고객에게 표시되는 정보 입력" />
                </Form.Item>
                <Form.Item name="guidanceText" label="사용 조건 안내문구" rules={[{ required: true, message: "사용 조건 안내문구를 입력하세요." }]}>
                    <TextArea rows={4} placeholder="고객에게 표시되는 정보 입력" />
                </Form.Item>
                <Form.Item name="condition" label="발급조건">
                    <Input placeholder="내부 확인용 정보 입력"></Input>
                </Form.Item>
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item
                            name="effectiveDate"
                            label="쿠폰 사용 시작일"
                            rules={[{ type: "object", required: true, message: "쿠폰 사용 시작일을 선택하세요." }]}
                            className="margin-thin"
                        >
                            <CustomDatePicker
                                value={effectiveDate !== undefined ? dayjs(effectiveDate.valueOf()) : undefined}
                                onChange={(value) =>
                                    setEffectiveDate(value ? Utils.utcToLocalTime(String(value).valueOf(), Common.FORMAT_DATE) + " 00:00:00" : undefined)
                                }
                                placeholder="시작일"
                                allowClear={false}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="expiredDate"
                            label="쿠폰 사용 만료일"
                            rules={[{ type: "object", required: true, message: "쿠폰 사용 만료일을 선택하세요." }]}
                            style={{ marginBottom: 0 }}
                            className="margin-thin"
                        >
                            <CustomDatePicker
                                value={expiredDate !== undefined ? dayjs(expiredDate.valueOf()) : undefined}
                                onChange={(value) =>
                                    setExpiredDate(value ? Utils.utcToLocalTime(String(value).valueOf(), Common.FORMAT_DATE) + " 23:59:59" : undefined)
                                }
                                placeholder="만료일"
                                allowClear={false}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="registerExpiryDate" label="쿠폰코드 등록 마감일" className="margin-thin">
                            <CustomDatePicker
                                value={registerExpiryDate !== undefined ? dayjs(registerExpiryDate.valueOf()) : undefined}
                                onChange={(value) =>
                                    setRegisterExpiryDate(value ? Utils.utcToLocalTime(String(value).valueOf(), Common.FORMAT_DATE) + " 23:59:59" : undefined)
                                }
                                placeholder="없을 시 미입력"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24} className={`${styles.note} ${styles.margin0}`}>
                        ※프로모션 회원 쿠폰일 경우 쿠폰 사용 만료일까지 매월1일 쿠폰자동생성, 매월말 쿠폰자동삭제 됩니다.
                    </Col>
                </Row>
                <Divider style={{ borderTopColor: "#1C345442" }} />
                <h3>할인쿠폰</h3>
                <div className={styles.ml1}>
                    <div className={styles.labelWrap}>
                        <label htmlFor="" className={styles.subtitle}>
                            <span>&#183;</span> 금액 고정(1박당 적용가)
                        </label>
                        <Tooltip
                            title={
                                <div>
                                    ※적용가로 입력해 주세요. (예: 99,000원으로 입력 시 1박당 99,000원으로 정산함)
                                    <br />
                                    ※값이 0일 경우 무료 렌트로 처리합니다.
                                    <br />
                                    ※쿠폰은 주말 -&gt; 평일 순으로 차감 됨.
                                </div>
                            }
                        >
                            <InfoCircleFilled />
                        </Tooltip>
                    </div>
                    <div className={styles.ml1}>
                        <Row gutter={24} className="divider">
                            <Col span={24} className="no-divider">
                                <Form.Item name="couponTypeDays" className="margin-thin">
                                    <Radio.Group>
                                        <Radio value="">미적용</Radio>
                                        <Radio value="DAYS">적용</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Space>
                                    <span className={styles.label}>평일</span>
                                    <Form.Item name="weekdayDiscountedPrice" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                    <span className={styles.label}>원</span>
                                </Space>
                            </Col>
                            <Col span={8}>
                                <Space>
                                    <span className={styles.label}>주말</span>
                                    <Form.Item name="weekendDiscountedPrice" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                    <span className={styles.label}>원</span>
                                </Space>
                            </Col>
                            <Col span={8} className="no-divider">
                                <Space>
                                    <span className={styles.label}>적용일 수</span>
                                    <Form.Item name="effectiveDays" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} style={{ width: 40 }} />
                                    </Form.Item>
                                    <span className={styles.label}>박</span>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                    <div className={`${styles.labelWrap} ${styles.mt1}`}>
                        <label htmlFor="" className={styles.subtitle}>
                            <span>&#183;</span> 금액 할인(최종가에서 뺄 값)
                        </label>
                        <Tooltip
                            title={
                                <div>
                                    ※여기서 말하는 최종가는 렌트비의 최종가임
                                    <br />
                                    &nbsp; &nbsp;(중복 할인 쿠폰으로 쓸 경우 단독 쿠폰 적용가에서 정산)
                                    <br />
                                    ※정산 예1) 10%로 입력할 경우 최종가에서 10% 할인 정산함. ※정산 예2) 5,000원으로 입력할 경우 최종가에서 5,000원 할인 정산함.
                                    <br />
                                    ※정산 예3) 통합에 입력한 것은 평일/주말 구분없이 최종가에서 정산하고, 평일/주말 한 곳에만 작성할 경우 작성된 곳만 처리함.
                                    <br />
                                    ※정산 예4) 통합에 값이 입력될 경우 평일/주말은 입력하지 아니함.
                                    <br />
                                    ※정산 예5) 평일 또는 주말 쿠폰인데, 고객이 평일+주말로 예약할 경우 해당하는 조건에서만 정산처리함.
                                </div>
                            }
                        >
                            <InfoCircleFilled />
                        </Tooltip>
                    </div>
                    <div className={styles.ml1}>
                        <Row gutter={24} className="divider">
                            <Col span={9}>
                                <Form.Item name="couponTypeTotal" className="margin-thin">
                                    <Radio.Group>
                                        <Radio value="">미적용</Radio>
                                        <Radio value="TOTAL">적용</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col span={15}>
                                <Space>
                                    <span className={styles.label}>단위</span>
                                    <Form.Item name="discountUnit" className="margin-thin">
                                        <Select
                                            options={[
                                                { value: "PERCENT", label: "%" },
                                                { value: "KRW", label: "원" },
                                            ]}
                                        />
                                    </Form.Item>
                                </Space>
                            </Col>
                        </Row>
                        <Row gutter={24} className="divider">
                            <Col span={12} className="no-divider">
                                <Space style={{ width: "100%" }}>
                                    <span className={styles.label}>통합</span>
                                    <Form.Item name="discountPrice" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                </Space>
                            </Col>
                            <Col span={12} className="no-divider" />
                            <Col span={12}>
                                <Space style={{ width: "100%" }}>
                                    <span className={styles.label}>평일</span>
                                    <Form.Item name="weekdayDiscountAmount" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                </Space>
                            </Col>
                            <Col span={12} className="no-divider">
                                <Space>
                                    <span className={styles.label}>주말</span>
                                    <Form.Item name="weekendDiscountAmount" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                    <div className={`${styles.labelWrap} ${styles.mt1}`}>
                        <label htmlFor="" className={styles.subtitle}>
                            <span>&#183;</span> 쿠폰 적용일 수 외 금액(1박당 적용 값)
                        </label>
                        <Tooltip
                            title={
                                <div>
                                    ※적용가로 입력해 주세요. (예: 99,000원으로 입력 시 1박당 99,000원으로 정산함)
                                    <br />
                                    ※값이 없거나 0일 경우 정상가로 처리합니다.
                                </div>
                            }
                        >
                            <InfoCircleFilled />
                        </Tooltip>
                    </div>
                    <div className={styles.ml1}>
                        <Row gutter={24} className="divider">
                            <Col span={12}>
                                <Space style={{ width: "100%" }}>
                                    <span className={styles.label}>평일</span>
                                    <Form.Item name="remainingDaysPrice" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                    <span className={styles.label}>원</span>
                                </Space>
                            </Col>
                            <Col span={12} className="no-divider">
                                <Space>
                                    <span className={styles.label}>주말</span>
                                    <Form.Item name="remainingDaysWeekendPrice" className="margin-thin">
                                        <InputNumber formatter={numberFormatter} controls={false} />
                                    </Form.Item>
                                    <span className={styles.label}>원</span>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                </div>
                <Divider style={{ borderTopColor: "#1C345442" }} />
                <h3>무료 대여 쿠폰</h3>
                <div className={`${styles.labelWrap}`}>
                    <label htmlFor="" className={styles.subtitle}>
                        대여품목 선택
                    </label>
                    <Tooltip title={<div>※선택한 유료 대여품목을 무료로 대여함.</div>}>
                        <InfoCircleFilled />
                    </Tooltip>
                </div>
                <Form.Item name="rentPaidItemId" className="margin-thin">
                    <Select placeholder="대여품목 선택" allowClear>
                        {rentPaidItems
                            ?.filter((item) => item.unavailable !== true)
                            .map((item: Types.RentPaidItemDto) => (
                                <Option key={item.rentPaidItemId} value={item.rentPaidItemId}>
                                    <span>{item.rentPaidItemName}</span>
                                </Option>
                            ))}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default ModalCoupon;
